import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { errorMessage, useTRPC } from "../../lib/api";
import { Button, ErrorText, PageHeader, Section, StatusText } from "../../ui";
import { safeHref } from "../chat/markdown";
import { fromDateInput } from "../tasks/model";
import { memberLabel, useMembers } from "../tasks/use-task-actions";
import { formatMeetingTime } from "./model";
import { draftsFor, notesStatusText, type Member, type TaskDraft } from "./notes-model";

const field = "min-h-9 rounded-md border border-border bg-surface px-2 text-sm text-text disabled:opacity-60";

/**
 * A meeting's AI notes (WP-017): summary, decisions and action items. Each action item can become
 * a task (title, assignee and due date editable first; the assignee is pre-filled only when the
 * speaker's name matches exactly one member), one at a time or all at once. Tasks carry the
 * meeting as their source.
 */
export function MeetingNotesPage({ workspaceId, meetingId }: { workspaceId: string; meetingId: string }) {
  const trpc = useTRPC();
  const meeting = useQuery(trpc.meet.get.queryOptions({ workspaceId, meetingId }));
  const members = useMembers(workspaceId);

  if (meeting.isPending) return <StatusText>Loading the meeting…</StatusText>;
  if (meeting.isError) return <ErrorText>The meeting could not be loaded: {errorMessage(meeting.error)}</ErrorText>;
  const m = meeting.data;
  const href = safeHref(m.meetUri);
  const notes = m.notes;

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <PageHeader title={m.title}>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-muted">{formatMeetingTime(m)}</span>
          {href === null ? null : (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent underline-offset-2 hover:underline">
              Join
            </a>
          )}
        </div>
      </PageHeader>
      {notes.status !== "ready" ? (
        <Section title="Notes">
          <StatusText>{notesStatusText(notes)}</StatusText>
        </Section>
      ) : (
        <>
          <Section title="Summary" description="Written by Claude from the meeting's transcript; check it before you rely on it.">
            <p className="whitespace-pre-wrap text-sm text-text">{notes.summary}</p>
          </Section>
          <Section title="Decisions">
            {notes.decisions.length === 0 ? (
              <p className="text-sm text-muted">No decisions recorded.</p>
            ) : (
              <ul aria-label="Decisions" className="list-disc pl-5 text-sm text-text">
                {notes.decisions.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            )}
          </Section>
          <ActionItems workspaceId={workspaceId} meetingId={meetingId} title={m.title} items={notes.actionItems} members={members.data === undefined ? [] : members.data} />
        </>
      )}
    </div>
  );
}

type Row = TaskDraft & { state: "draft" | "creating" | "created" | "failed"; error: string | null };

function ActionItems(props: { workspaceId: string; meetingId: string; title: string; items: Parameters<typeof draftsFor>[0]; members: readonly Member[] }) {
  const { workspaceId, meetingId, title, items, members } = props;
  const trpc = useTRPC();
  const create = useMutation(trpc.tasks.create.mutationOptions());
  const [rows, setRows] = useState<Row[]>(() => draftsFor(items, members).map((d) => ({ ...d, state: "draft", error: null })));

  // Members arrive after the first render: fill in assignees nobody has touched yet.
  useEffect(() => {
    if (members.length === 0) return;
    const matched = draftsFor(items, members);
    setRows((current) => current.map((r, i) => (r.state === "draft" && r.assigneeUid === "" ? { ...r, assigneeUid: matched[i]?.assigneeUid ?? "" } : r)));
  }, [items, members]);

  const patch = (i: number, p: Partial<Row>) => setRows((current) => current.map((r, j) => (j === i ? { ...r, ...p } : r)));

  async function createOne(i: number): Promise<void> {
    const row = rows[i];
    if (row === undefined || row.state === "creating" || row.state === "created" || row.title.trim().length === 0) return;
    patch(i, { state: "creating", error: null });
    try {
      await create.mutateAsync({
        workspaceId,
        title: row.title.trim(),
        notes: `From the meeting “${title}”.`,
        assigneeUid: row.assigneeUid === "" ? null : row.assigneeUid,
        due: fromDateInput(row.due),
        source: { kind: "meeting", ref: meetingId },
      });
      patch(i, { state: "created" });
    } catch (error) {
      patch(i, { state: "failed", error: errorMessage(error) });
    }
  }

  async function createAll(): Promise<void> {
    for (let i = 0; i < rows.length; i += 1) await createOne(i);
  }

  const open = rows.filter((r) => r.state !== "created").length;
  return (
    <Section title="Action items">
      {rows.length === 0 ? (
        <p className="text-sm text-muted">No action items.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <ul aria-label="Action items" className="flex flex-col gap-3">
            {rows.map((r, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2" data-state={r.state}>
                <input
                  aria-label={`Task title ${i + 1}`}
                  value={r.title}
                  onChange={(e) => patch(i, { title: e.target.value })}
                  disabled={r.state === "created" || r.state === "creating"}
                  className={`${field} min-w-48 flex-1`}
                  maxLength={300}
                />
                <select
                  aria-label={`Assignee ${i + 1}`}
                  value={r.assigneeUid}
                  onChange={(e) => patch(i, { assigneeUid: e.target.value })}
                  disabled={r.state === "created" || r.state === "creating"}
                  className={field}
                >
                  <option value="">Nobody</option>
                  {members.map((m) => (
                    <option key={m.uid} value={m.uid}>
                      {memberLabel(members, m.uid)}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  aria-label={`Due ${i + 1}`}
                  value={r.due}
                  onChange={(e) => patch(i, { due: e.target.value })}
                  disabled={r.state === "created" || r.state === "creating"}
                  className={field}
                />
                {r.state === "created" ? (
                  <span className="text-sm text-muted">Task created</span>
                ) : (
                  <Button onClick={() => void createOne(i)} disabled={r.state === "creating" || r.title.trim().length === 0}>
                    {r.state === "creating" ? "Creating…" : "Create task"}
                  </Button>
                )}
                {r.error === null ? null : <ErrorText>{r.error}</ErrorText>}
              </li>
            ))}
          </ul>
          {open > 1 ? (
            <div>
              <Button onClick={() => void createAll()} disabled={rows.some((r) => r.state === "creating")}>
                {`Create all (${open})`}
              </Button>
            </div>
          ) : null}
        </div>
      )}
    </Section>
  );
}
